import React, { useEffect, useState } from "react";
import { Button, Dialog, Label } from "usebeauty";
import classNames from "classnames";
import { useRoutes } from "@router/Router";
import { useOCRContext } from "@context/OCRContext";
import utils from "@common/utils";
import sdk from "@sdk";
import * as api from "@api";
import { Back, Close } from "@Icons";
import * as ocrUtils from "./utils";
import "./OCRPage.scss";

const OCRPage = () => {
  const { step, navigate } = useRoutes();
  const ocrContext = useOCRContext();
  const { ocrType } = ocrContext;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const scanHandler = async () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ocrResultCallBack = async (result: any) => {
      try {
        if (result.result !== "success") throw result;

        ocrContext.handlers.setResult(result.review_result);
        const base64 = result.review_result.ocr_face_image;

        const croppedImage = await api.croppedFace({ image: base64, resizeWidth: 150, ratio: 0.25 });
        const imageA = croppedImage?.data.image_data;
        if (!imageA) throw new Error(`얼굴이 인식되지 않습니다.<br /> 다시 시도해 주세요.`);

        ocrContext.handlers.setCroppedImage(imageA);

        navigate("next");
      } catch (e) {
        if (utils.isOCRError(e)) {
          const return_code = e.api_response?.result_code;
          const return_msg = e.api_response?.result_message;
          const errorMessage = e.message || "";
          const errorDetail = e.review_result?.error_detail || "";
          setError(`${return_code}<br />${return_msg}<br /><br />${errorMessage}<br />${errorDetail}`); // prettier-ignore
        } else if (utils.isFaceServerError(e) && e.response?.data?.return_msg.return_code) {
          const return_code = e.response?.data?.return_msg.return_code;
          const return_msg = e.response?.data?.return_msg.return_msg;
          setError(`${return_code}<br />${return_msg}<br /><br />${e.message || ""}`); // prettier-ignore
        } else {
          setError(`${(e as Error).message}`);
        }
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onProgressCallback = async (...args: any) => {
      setLoading(false);
      const res = ocrUtils.mergeFromResponse(args);

      const { customUI, uiPosition } = res;
      const { message, process } = ocrUtils.getProgress(sdk.ocr, res);
      const loading = process === "loading";

      // Loading UI 처리
      const preview = process === "preview";
      setLoading(preview);

      if (uiPosition === "bottom") {
        ocrUtils.renderProgress({
          message,
          loading,
          uiPosition,
          customUI,
        });
        await sdk.ocr.__sleep(1); // for UI update
      }
    };

    // prevent error when previous ocr instance is not destroyed yet
    try {
      sdk.ocr.stopOCR();
    } catch (e) {
      // nothing to do
    }
    await sdk.ocr.startOCR(ocrType, ocrResultCallBack, ocrResultCallBack, onProgressCallback);
  };

  const cleanup = () => {
    sdk.ocr.stopOCR();
    setError("");
    setLoading(false);
  };

  const prevHandler = () => {
    cleanup();
    navigate("prev");
  };

  const modalCloseHandler = () => {
    cleanup();
    scanHandler();
  };

  useEffect(() => {
    setLoading(true);
    scanHandler();
  }, []);

  return (
    <>
      <Dialog
        open={!!error}
        content={() => {
          return (
            <Dialog.Base id="dialog-ocr-error" st={{ width: 300 }}>
              <Dialog.Summary closeHandler={modalCloseHandler}></Dialog.Summary>
              <Dialog.Content st={{ height: 220 }}>
                <div dangerouslySetInnerHTML={{ __html: error }}></div>
              </Dialog.Content>
            </Dialog.Base>
          );
        }}></Dialog>
      <div className="alchera-demo-ocr-scan">
        <div className={classNames("navigation", { loading })}>
          {step.prev && (
            <i onClick={prevHandler}>
              <Back st={{ color: "#000038", fontSize: "16px" }} />
            </i>
          )}
          <Label text={step.label}></Label>
          <i onClick={() => window.location.reload()}>
            <Close st={{ color: "#000038", fontSize: "16px" }} />
          </i>
        </div>

        {loading && (
          <div className="alchera-demo-loading">
            <div className="spinner">
              {Array.from(new Array(12)).map((_, i) => (
                <div key={i}></div>
              ))}
            </div>
          </div>
        )}

        <div data-useb-ocr="ocr" style={{ display: loading ? "none" : "block" }}>
          <div data-useb-ocr="bottomUI">
            <div id="bottom-ui-bg" style={{ width: "100%" }}>
              <span id="bottom-ui-loading"></span>
              <span id="bottom-ui-text-msg" className="text-info">
                <p className="bottom-ui-description">신분증 종류 선택 후, 화면의 가이드 안에</p>
                <p className="bottom-ui-description">꽉 차게 배치하면 자동으로 촬영됩니다.</p>
              </span>
            </div>
          </div>
        </div>

        <div className="footer">
          <div className="steps">
            <span>Step 1</span>
            <div className="step">
              <div className="level current"></div>
              <div className="level"></div>
              <div className="level"></div>
              <div className="level"></div>
            </div>
          </div>
          <Button ripple={false}>촬영 중</Button>
        </div>
      </div>
    </>
  );
};

export default OCRPage;
